/**
 * AttestationsView (P131.2, #483) -- the DRRA quarterly attestation
 * programme surface.
 *
 *   - Cycle picker chips (status + per-status counts) + admin create.
 *   - Selected-cycle attestation table with status pills.
 *   - Raise-attestation form (preparer; only while the cycle is open).
 *   - Per-row workflow buttons gated by status + the user's position.
 *   - Inline expand showing the frozen snapshot + approver comments.
 *
 * CAP-shaped: no generic workflow engine -- the backend owns the state
 * machine, the UI just offers the legal next action.
 */
import { formatDate } from "shared/time/formatDate";
import { api, type ApiError } from "shared/api/client";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Explainer } from "shared/components/Explainer";
import { useTranslation } from "react-i18next";

import { outcomeCategoryLabel } from "shared/vocab/workflowState";

import { useDateInputLang } from "shared/i18n/dateInputLang";

import {
  useAttestationCycles,
  useAttestationIbsOptions,
  useAttestations,
  useCreateAttestation,
  useCreateAttestationCycle,
  usePatchAttestation,
  useTransitionAttestation,
  useTransitionCycle,
} from "framework/api/attestation_hooks";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import type {
  Attestation,
  AttestationCycle,
  AttestationStatus,
} from "shared/api/types";
import { useToast } from "shared/toast/ToastProvider";

const STATUS_PILL: Record<AttestationStatus, string> = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

const CYCLE_PILL: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700",
  open: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-200 text-slate-600",
};

const BRASS_BTN =
  "rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy " +
  "hover:bg-brand-brassDark disabled:opacity-50";
const OUTLINE_BTN =
  "rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-sm " +
  "text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50";

export function AttestationsView() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const positions = me.data?.positions ?? [];
  const canPrepare = isAdmin || positions.includes("preparer");
  const canApprove = isAdmin || positions.includes("approver");

  const cyclesQ = useAttestationCycles();
  const cycles = cyclesQ.data ?? [];
  /* Round 16 G8 -- My Work links here with `?cycle=`, and the page
     opened the first cycle instead. The URL picks the cycle until the
     reader picks another. */
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("cycle");
  const setSelectedId = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set("cycle", id);
        else next.delete("cycle");
        return next;
      },
      { replace: true },
    );
  const selected = cycles.find((c) => c.id === selectedId) ?? cycles[0] ?? null;

  return (
    <article
      data-testid="attestations-view"
      className="mx-auto max-w-5xl space-y-6 p-4"
    >
      <header className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-wide text-slate-500">
          {t("modules.framework.attestations.rubric")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="attestation"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("modules.framework.attestations.heading")}
        </h1>
        <p className="text-sm text-slate-600">
          {t("modules.framework.attestations.intro")}
        </p>
      </header>

      <Explainer termKey="drra" className="max-w-3xl" />

      {/* A2 -- the page instructs "Open a cycle, attest each important
          business service, and sign it off" and gave the compliance
          analyst no verb, because the form was admin-gated while the
          instruction was addressed to her. Opening the quarter's work
          is the compliance function's own job. */}
      {canPrepare && <CreateCycleForm />}

      <CyclePicker
        cycles={cycles}
        selectedId={selected?.id ?? null}
        onSelect={setSelectedId}
        loading={cyclesQ.isLoading}
        error={cyclesQ.error}
      />

      {selected && (
        <CyclePanel
          cycle={selected}
          isAdmin={isAdmin}
          canPrepare={canPrepare}
          canApprove={canApprove}
        />
      )}
    </article>
  );
}

// ── Cycle picker ─────────────────────────────────────────────────────

function CyclePicker({
  cycles,
  selectedId,
  onSelect,
  loading,
  error,
}: {
  cycles: AttestationCycle[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
  error?: ApiError | null;
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <p className="text-sm text-slate-500">
        {t("modules.framework.attestations.loading")}
      </p>
    );
  }
  // B5 -- a refused request rendered as "No attestation cycles yet",
  // so a Service Owner was told his bank had no attestation programme
  // when he simply could not see it. Say which it is.
  if (error) {
    return (
      <p data-testid="attestations-error" className="text-sm text-rose-700">
        {error.status === 403
          ? t("modules.framework.attestations.forbidden")
          : (error.detail ?? t("modules.framework.attestations.loadFailed"))}
      </p>
    );
  }
  if (cycles.length === 0) {
    return (
      <p data-testid="attestations-empty" className="text-sm text-slate-500">
        {t("modules.framework.attestations.noCycles")}
      </p>
    );
  }
  return (
    <div data-testid="attestation-cycle-picker" className="flex flex-wrap gap-2">
      {cycles.map((c) => {
        const active = c.id === selectedId;
        return (
          <button
            key={c.id}
            type="button"
            data-testid={`attestation-cycle-chip-${c.id}`}
            data-active={active ? "true" : "false"}
            onClick={() => onSelect(c.id)}
            className={`rounded-lg border px-3 py-2 text-start ${
              active
                ? "border-brand-brass bg-brand-brass/10"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <span className="block text-sm font-medium text-slate-800">{c.name}</span>
            <span className="mt-1 flex items-center gap-2 text-xs">
              <span className={`rounded px-1.5 py-0.5 ${CYCLE_PILL[c.status] ?? ""}`}>
                {t(`modules.framework.attestations.cycleStatus.${c.status}`)}
              </span>
              <span className="text-slate-500">
                {c.counts.approved}/{c.attestation_count}{" "}
                {t("modules.framework.attestations.approvedShort")}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Create cycle (admin) ─────────────────────────────────────────────

function CreateCycleForm() {
  const { t } = useTranslation();
  const dateInputLang = useDateInputLang();
  const toast = useToast();
  const create = useCreateAttestationCycle();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [dueDate, setDueDate] = useState("");

  const ready = name.trim() && periodStart && periodEnd && dueDate;

  if (!open) {
    return (
      <button
        type="button"
        data-testid="attestation-new-cycle-toggle"
        className={OUTLINE_BTN}
        onClick={() => setOpen(true)}
      >
        {t("modules.framework.attestations.newCycle")}
      </button>
    );
  }

  return (
    <form
      data-testid="attestation-new-cycle-form"
      className="space-y-3 rounded-lg border border-slate-200 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          {
            name: name.trim(),
            period_start: periodStart,
            period_end: periodEnd,
            due_date: dueDate,
          },
          {
            onSuccess: () => {
              toast.push({
                tone: "success",
                message: t("modules.framework.attestations.cycleCreated"),
              });
              setOpen(false);
              setName("");
            },
            onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
          },
        );
      }}
    >
      <input
        dir="auto"
        data-testid="attestation-cycle-name"
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
        placeholder={t("modules.framework.attestations.cycleNamePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="grid grid-cols-3 gap-2">
        <label className="text-xs text-slate-500">
          {t("modules.framework.attestations.periodStart")}
          <input
            type="date"
            lang={dateInputLang}
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-500">
          {t("modules.framework.attestations.periodEnd")}
          <input
            type="date"
            lang={dateInputLang}
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-500">
          {t("modules.framework.attestations.dueDate")}
          <input
            type="date"
            lang={dateInputLang}
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          className={BRASS_BTN}
          disabled={!ready || create.isPending}
        >
          {t("modules.framework.attestations.create")}
        </button>
        <button type="button" className={OUTLINE_BTN} onClick={() => setOpen(false)}>
          {t("modules.framework.attestations.cancel")}
        </button>
      </div>
    </form>
  );
}

// ── Cycle panel (transitions + attestation table) ────────────────────

function CyclePanel({
  cycle,
  isAdmin,
  canPrepare,
  canApprove,
}: {
  cycle: AttestationCycle;
  isAdmin: boolean;
  canPrepare: boolean;
  canApprove: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const transitionCycle = useTransitionCycle();
  const attestationsQ = useAttestations(cycle.id);
  const rows = attestationsQ.data ?? [];

  function cycleAction(action: "open" | "close") {
    transitionCycle.mutate(
      { cycleId: cycle.id, action },
      {
        onSuccess: () =>
          toast.push({
            tone: "success",
            message: t(`modules.framework.attestations.cycle_${action}ed`),
          }),
        onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
      },
    );
  }

  return (
    <section
      data-testid="attestation-cycle-panel"
      className="space-y-4 rounded-lg border border-slate-200 p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-medium text-slate-900">{cycle.name}</h2>
          <p className="text-xs text-slate-500">
            {cycle.period_start} {t("screens.attestationsView.text")} {cycle.period_end}{" "}
            · {t("modules.framework.attestations.due")} {cycle.due_date}
          </p>
        </div>
        {/* B1 -- the verb that opens the round she created. Gating this
            on admin left her a Draft she could not leave, under a
            sentence telling her to open one.
            Close is not hers and is gated separately below: opening the
            quarter's work is routine compliance work, declaring it
            finished is not. */}
        {(canPrepare || isAdmin) && (
          <div className="flex gap-2">
            {cycle.status === "draft" && (
              <button
                type="button"
                data-testid="attestation-cycle-open"
                className={BRASS_BTN}
                onClick={() => cycleAction("open")}
              >
                {t("modules.framework.attestations.openCycle")}
              </button>
            )}
            {cycle.status === "open" && isAdmin && (
              <button
                type="button"
                data-testid="attestation-cycle-close"
                className={OUTLINE_BTN}
                onClick={() => cycleAction("close")}
              >
                {t("modules.framework.attestations.closeCycle")}
              </button>
            )}
          </div>
        )}
      </div>

      <CountsStrip counts={cycle.counts} />

      {cycle.status === "open" && canPrepare && (
        <RaiseAttestationForm cycleId={cycle.id} existing={rows} />
      )}

      {rows.length === 0 ? (
        <p data-testid="attestation-rows-empty" className="text-sm text-slate-500">
          {t("modules.framework.attestations.noAttestations")}
        </p>
      ) : (
        <ul data-testid="attestation-rows" className="space-y-2">
          {rows.map((a) => (
            <AttestationRow
              key={a.id}
              att={a}
              canPrepare={canPrepare}
              canApprove={canApprove}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function CountsStrip({ counts }: { counts: AttestationCycle["counts"] }) {
  const { t } = useTranslation();
  const order: AttestationStatus[] = ["draft", "submitted", "approved", "rejected"];
  return (
    <div data-testid="attestation-counts" className="flex flex-wrap gap-2 text-xs">
      {order.map((s) => (
        <span
          key={s}
          className={`rounded px-2 py-1 ${STATUS_PILL[s]}`}
          data-testid={`attestation-count-${s}`}
        >
          {t(`modules.framework.attestations.status.${s}`)}: {counts[s]}
        </span>
      ))}
    </div>
  );
}

// ── Raise attestation (preparer) ─────────────────────────────────────

function RaiseAttestationForm({
  cycleId,
  existing,
}: {
  cycleId: string;
  existing: Attestation[];
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const create = useCreateAttestation();
  const ibsQ = useAttestationIbsOptions(true);
  const taken = new Set(existing.map((a) => a.ibs_service_id));
  const options = (ibsQ.data ?? []).filter((o) => !taken.has(o.id));
  const [ibsId, setIbsId] = useState("");
  const [text, setText] = useState("");

  return (
    <form
      data-testid="attestation-raise-form"
      className="flex flex-wrap items-end gap-2 rounded border border-slate-200 bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ibsId) return;
        create.mutate(
          {
            cycle_id: cycleId,
            ibs_service_id: ibsId,
            attestation_text: text.trim() || null,
          },
          {
            onSuccess: () => {
              toast.push({
                tone: "success",
                message: t("modules.framework.attestations.raised"),
              });
              setIbsId("");
              setText("");
            },
            onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
          },
        );
      }}
    >
      <label className="text-xs text-slate-500">
        {t("modules.framework.attestations.ibs")}
        <select
          data-testid="attestation-raise-ibs"
          className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-sm"
          value={ibsId}
          onChange={(e) => setIbsId(e.target.value)}
        >
          <option value="">{t("modules.framework.attestations.selectIbs")}</option>
          <optgroup label={t("modules.framework.attestations.servicesGroup")}>
            {options
              .filter((o) => o.service_class !== "internal")
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
          </optgroup>
          {/* Round 16 L4 -- a function was listed as an IBS under
              "attest each important business service". */}
          {options.some((o) => o.service_class === "internal") && (
            <optgroup label={t("modules.framework.attestations.functionsGroup")}>
              {options
                .filter((o) => o.service_class === "internal")
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </optgroup>
          )}
        </select>
      </label>
      <input
        dir="auto"
        data-testid="attestation-raise-text"
        className="min-w-[16rem] flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
        placeholder={t("modules.framework.attestations.statementPlaceholder")}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <button type="submit" className={BRASS_BTN} disabled={!ibsId || create.isPending}>
        {t("modules.framework.attestations.raise")}
      </button>
    </form>
  );
}

// ── Attestation row ──────────────────────────────────────────────────

function AttestationRow({
  att,
  canPrepare,
  canApprove,
}: {
  att: Attestation;
  canPrepare: boolean;
  canApprove: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const transition = useTransitionAttestation();
  const patch = usePatchAttestation();
  /* Round 19 G41 -- from the letter, the attestation opened closed
     among the others. `?attestation=` opens the one named. */
  const [linkParams] = useSearchParams();
  const named = linkParams.get("attestation") === att.id;
  const [expanded, setExpanded] = useState(named);
  const rowRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (named) rowRef.current?.scrollIntoView?.({ block: "center" });
  }, [named]);
  const [rejectComments, setRejectComments] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [statement, setStatement] = useState(att.attestation_text ?? "");
  // An attestation IS the statement. Raised without one it can never be
  // submitted -- the backend has always said so, in a 409 the operator
  // had no way to act on because nothing offered them an edit.
  const hasStatement = Boolean((att.attestation_text ?? "").trim());
  // Round 16 G7 -- maker and checker are different people. The
  // administrator was offered the approval of the attestation he had
  // just submitted, and gave it.
  const me = useMe();
  const myEmail = (me.data?.email ?? "").trim().toLowerCase();
  const ownSubmission =
    myEmail !== "" && myEmail === (att.submitter_email ?? "").trim().toLowerCase();

  function act(action: "submit" | "approve" | "reject" | "reopen") {
    transition.mutate(
      {
        id: att.id,
        action,
        ...(action === "reject" ? { comments: rejectComments } : {}),
      },
      {
        onSuccess: () => {
          toast.push({
            tone: action === "reject" ? "warning" : "success",
            message: t(`modules.framework.attestations.acted.${action}`),
          });
          setRejecting(false);
          setRejectComments("");
        },
        onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
      },
    );
  }

  const snapshotIbs =
    (att.snapshot?.ibs as Record<string, unknown> | undefined) ?? null;

  return (
    <li
      ref={rowRef}
      data-testid={`attestation-row-${att.id}`}
      data-named={named ? "true" : undefined}
      className={`rounded border p-3 ${named ? "border-brand-brass" : "border-slate-200"}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          data-testid={`attestation-expand-${att.id}`}
          aria-expanded={expanded}
          className="text-start"
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="text-sm font-medium text-slate-800">
            {att.ibs_name ?? att.ibs_service_id}
          </span>
          {/* Round 12 H5 -- the margin drew the gap and left the text
              reading "Retail paymentsDraft". */}
          {" \u00b7 "}
          <span
            className={`ms-2 rounded px-2 py-0.5 text-xs ${STATUS_PILL[att.status]}`}
            data-testid={`attestation-status-${att.id}`}
          >
            {t(`modules.framework.attestations.status.${att.status}`)}
          </span>
        </button>
        <div className="flex flex-wrap gap-2">
          {att.status === "draft" && canPrepare && (
            <>
              <button
                type="button"
                data-testid={`attestation-submit-${att.id}`}
                className={BRASS_BTN}
                disabled={!hasStatement}
                title={
                  hasStatement
                    ? undefined
                    : t("modules.framework.attestations.needsStatement")
                }
                onClick={() => act("submit")}
              >
                {t("modules.framework.attestations.submit")}
              </button>
              {!hasStatement && (
                <span
                  data-testid={`attestation-needs-statement-${att.id}`}
                  className="self-center text-xs text-amber-700"
                >
                  {t("modules.framework.attestations.needsStatement")}
                </span>
              )}
            </>
          )}
          {att.status === "submitted" && canApprove && ownSubmission && (
            <span
              data-testid={`attestation-own-submission-${att.id}`}
              className="self-center text-xs text-slate-600"
            >
              {t("modules.framework.attestations.ownSubmission")}
            </span>
          )}
          {att.status === "submitted" && canApprove && !ownSubmission && (
            <>
              <button
                type="button"
                data-testid={`attestation-approve-${att.id}`}
                className={BRASS_BTN}
                onClick={() => act("approve")}
              >
                {t("modules.framework.attestations.approve")}
              </button>
              <button
                type="button"
                data-testid={`attestation-reject-${att.id}`}
                className={OUTLINE_BTN}
                onClick={() => setRejecting((v) => !v)}
              >
                {t("modules.framework.attestations.reject")}
              </button>
            </>
          )}
          {att.status === "rejected" && canPrepare && (
            <button
              type="button"
              data-testid={`attestation-reopen-${att.id}`}
              className={OUTLINE_BTN}
              onClick={() => act("reopen")}
            >
              {t("modules.framework.attestations.reopen")}
            </button>
          )}
        </div>
      </div>

      {rejecting && (
        <div className="mt-2 flex gap-2">
          <input
            dir="auto"
            data-testid={`attestation-reject-comments-${att.id}`}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder={t("modules.framework.attestations.rejectPlaceholder")}
            value={rejectComments}
            onChange={(e) => setRejectComments(e.target.value)}
          />
          <button
            type="button"
            className={BRASS_BTN}
            disabled={!rejectComments.trim()}
            onClick={() => act("reject")}
          >
            {t("modules.framework.attestations.confirmReject")}
          </button>
        </div>
      )}

      {expanded && (
        <dl
          data-testid={`attestation-detail-${att.id}`}
          className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-600"
        >
          {/* What you are attesting TO. The snapshot only exists from
              submit onward, so a draft that showed only the snapshot
              showed the signatory nothing to sign. */}
          {att.status === "draft" && att.ibs_summary && (
            <p data-testid={`attestation-current-${att.id}`}>
              {t("modules.framework.attestations.currentLabel")}:{" "}
              {/* ROUND 15 G5 -- the code beside the name said the same
                  thing twice, once in a form nobody reads. */}
              {String(att.ibs_summary.name ?? att.ibs_summary.code)} ·{" "}
              {outcomeCategoryLabel(String(att.ibs_summary.outcome_category))}{" "}
              {/* Round 18 G28 -- in words, as the snapshot says it: "MVB ✗"
                  read before submit. */}
              ·{" "}
              {att.ibs_summary.mvb_flag
                ? t("modules.framework.attestations.inMvb")
                : t("modules.framework.attestations.notInMvb")}
            </p>
          )}

          {att.status === "draft" && canPrepare ? (
            <div className="space-y-1">
              <label
                className="block text-[10px] uppercase tracking-wider text-slate-500"
                htmlFor={`attestation-statement-${att.id}`}
              >
                {t("modules.framework.attestations.statementLabel")}
              </label>
              <textarea
                dir="auto"
                id={`attestation-statement-${att.id}`}
                data-testid={`attestation-statement-${att.id}`}
                rows={3}
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                placeholder={t("modules.framework.attestations.statementPlaceholder")}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
              <button
                type="button"
                data-testid={`attestation-save-statement-${att.id}`}
                className={OUTLINE_BTN}
                disabled={patch.isPending || statement === (att.attestation_text ?? "")}
                onClick={() =>
                  patch.mutate(
                    { id: att.id, attestation_text: statement.trim() || null },
                    {
                      onSuccess: () =>
                        toast.push({
                          tone: "success",
                          message: t("modules.framework.attestations.statementSaved"),
                        }),
                      onError: (e) =>
                        toast.push({
                          tone: "error",
                          message: e.detail ?? "error",
                        }),
                    },
                  )
                }
              >
                {t("modules.framework.attestations.saveStatement")}
              </button>
            </div>
          ) : (
            att.attestation_text && (
              <p className="text-slate-700">{att.attestation_text}</p>
            )
          )}
          {att.comments && (
            <p className="text-rose-700">
              {t("modules.framework.attestations.rejectionComments")}: {att.comments}
            </p>
          )}
          {/* Round 14 G, sixth round -- the snapshot printed a raw key
              ("ibs.demo.aml_str") and the record carried no signatures
              block, so the approved view read like a form rather than
              an attestation somebody had signed. The service is named
              in words, its code kept beside it for the auditor who
              matches it to a register, and who signed what, and when,
              is set out as its own block. */}
          {snapshotIbs ? (
            <p data-testid={`attestation-snapshot-${att.id}`}>
              {t("modules.framework.attestations.snapshotLabel")}:{" "}
              {/* Round 18 G28 -- the raw key ("ibs.demo.aml_str") beside
                  the name; the name alone, as the current line says it. */}
              {String(snapshotIbs.name ?? att.ibs_name ?? snapshotIbs.code)} ·{" "}
              {outcomeCategoryLabel(String(snapshotIbs.outcome_category))} ·{" "}
              {snapshotIbs.mvb_flag
                ? t("modules.framework.attestations.inMvb")
                : t("modules.framework.attestations.notInMvb")}
            </p>
          ) : (
            <p className="italic text-slate-400">
              {t("modules.framework.attestations.snapshotPending")}
            </p>
          )}
          {/* Round 18 G28 -- the submitter is recorded when the
              attestation is raised; a draft has no signatures yet. */}
          {att.status !== "draft" &&
            (att.submitter_name || att.approver_name || att.status === "submitted") && (
              <div
                data-testid={`attestation-signatures-${att.id}`}
                className="mt-1 rounded border border-slate-200 bg-slate-50 px-2 py-1.5"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {t("modules.framework.attestations.signatures")}
                </p>
                {/* ROUND 15 G5 -- "Attested by Noura on" with no date,
                  before submit: a name is not a signature until the
                  moment it was given exists. */}
                {att.submitter_name && att.submitted_at && (
                  <p>
                    {t("modules.framework.attestations.signedAttested", {
                      who: att.submitter_name,
                      when: formatDate(att.submitted_at),
                    })}
                  </p>
                )}
                {att.status === "approved" && att.approver_name && (
                  <p>
                    {t("modules.framework.attestations.signedApproved", {
                      who: att.approver_name,
                      when: formatDate(att.approved_at),
                    })}
                  </p>
                )}
                {att.status === "rejected" && att.approver_name && (
                  <p>
                    {t("modules.framework.attestations.signedRejected", {
                      who: att.approver_name,
                      when: formatDate(att.rejected_at),
                    })}
                  </p>
                )}
                {att.status === "submitted" && (
                  <p className="italic text-slate-500">
                    {t("modules.framework.attestations.awaitingApprover")}
                  </p>
                )}
              </div>
            )}
          {/* Round 18 T10 -- the letter and the plan, from here. */}
          <PlansOnService attestationId={att.id} />
        </dl>
      )}
    </li>
  );
}

interface PlanOnService {
  cap_id: string;
  cap_title: string;
  cap_state: string;
  letter_id: string;
  letter_title: string;
  finding_title: string;
}

/** Round 18 T10 -- the plans answering a finding on the attested
 *  service, each with the regulator's letter it came from. The link is
 *  the recorded one: the plan answers a finding that sits on this
 *  service, a service supporting it, or a configuration item linked to
 *  one. */
export function PlansOnService({ attestationId }: { attestationId: string }) {
  const { t } = useTranslation();
  const plans = useQuery({
    queryKey: ["attestation-links", "plans", attestationId],
    queryFn: () =>
      api.get<PlanOnService[]>(
        `/v1/attestation-links/attestation/${attestationId}/plans`,
      ),
  });
  const rows = Array.isArray(plans.data) ? plans.data : [];
  if (plans.isLoading) return null;
  return (
    <div data-testid={`attestation-plans-${attestationId}`} className="pt-2">
      <dt className="font-medium text-slate-700">
        {t("modules.framework.attestations.plansOnService")}
      </dt>
      {rows.length === 0 ? (
        <dd className="text-slate-500">
          {t("modules.framework.attestations.plansOnServiceNone")}
        </dd>
      ) : (
        rows.map((p) => (
          <dd key={p.cap_id} data-testid="attestation-plan-row">
            <Link
              to={`/framework/caps/${p.cap_id}`}
              className="text-brand-brassDark hover:underline"
            >
              <bdi>{p.cap_title}</bdi>
            </Link>{" "}
            ({t(`caps.detail.state_labels.${p.cap_state}`)}){" "}
            {t("modules.framework.attestations.fromLetter")}{" "}
            <Link
              to={`/framework/letters/${p.letter_id}`}
              className="text-brand-brassDark hover:underline"
            >
              <bdi>{p.letter_title}</bdi>
            </Link>
            <span className="block text-slate-500">
              {t("modules.framework.attestations.answersFinding", {
                finding: p.finding_title,
              })}
            </span>
          </dd>
        ))
      )}
    </div>
  );
}
